import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  BuildingOfficeIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import GooglePlacesLocationPicker from '@/components/ui/GooglePlacesLocationPicker'
import { useAuth } from '@/hooks/useAuth'
import { institutionsApi, type Institution } from '@/api/institutionsApi'
import toast from 'react-hot-toast'

const SchoolProfilePage: React.FC = () => {
  const { user } = useAuth()
  const [institution, setInstitution] = useState<Institution | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Location state
  const [latitude, setLatitude] = useState('')
  const [longitude, setLongitude] = useState('')
  const [locationAddress, setLocationAddress] = useState('')

  useEffect(() => {
    fetchInstitution()
  }, [user])

  const fetchInstitution = async () => {
    try {
      setLoading(true)
      const institutionId = (user as any)?.institutionId
      if (!institutionId) return

      const data = await institutionsApi.getInstitution(institutionId)
      setInstitution(data)

      // Pre-fill location if it exists
      if ((data as any).location?.latitude) {
        setLatitude(String((data as any).location.latitude))
        setLongitude(String((data as any).location.longitude))
        setLocationAddress((data as any).location.address || '')
      }
    } catch (error: any) {
      console.error('Failed to fetch institution:', error)
      toast.error('Failed to load school profile')
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateLocation = async () => {
    const institutionId = (user as any)?.institutionId
    if (!institutionId || !latitude || !longitude) {
      toast.error('Please select a location first')
      return
    }

    setSaving(true)
    try {
      await institutionsApi.updateInstitution(institutionId, {
        location: {
          latitude: parseFloat(latitude),
          longitude: parseFloat(longitude),
          address: locationAddress || `${institution?.settings?.address || ''}, ${institution?.settings?.city || ''}`
        }
      } as any)

      toast.success('School location updated successfully')
      fetchInstitution()
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update location'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-64 animate-pulse" />
        <div className="dashboard-card animate-pulse">
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    )
  }

  if (!institution) {
    return (
      <div className="text-center py-12">
        <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-4 text-lg font-medium text-gray-900">No institution found</h3>
        <p className="mt-2 text-gray-500">Your account is not linked to an institution.</p>
      </div>
    )
  }

  const hasLocation = latitude && longitude && parseFloat(latitude) !== 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <BuildingOfficeIcon className="h-8 w-8 text-blue-600 mr-3" />
          School Profile
        </h1>
        <p className="text-gray-600 mt-1">
          Manage your institution details and location
        </p>
      </div>

      {/* Institution Info Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card"
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Institution Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center space-x-3">
              <BuildingOfficeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="text-sm font-medium text-gray-900">{institution.name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <MapPinIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Address</p>
                <p className="text-sm font-medium text-gray-900">
                  {institution.settings?.address || 'Not set'}, {institution.settings?.city || ''}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <PhoneIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Phone</p>
                <p className="text-sm font-medium text-gray-900">{institution.settings?.phone || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <EnvelopeIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-sm font-medium text-gray-900">{institution.settings?.email || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <GlobeAltIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Plan</p>
                <p className="text-sm font-medium text-gray-900 capitalize">{institution.subscriptionPlan}</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Location Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="dashboard-card"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <MapPinIcon className="h-5 w-5 text-blue-600 mr-2" />
                School Location
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Set your school's GPS coordinates for transport tracking
              </p>
            </div>
            {hasLocation && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                Location set
              </span>
            )}
          </div>

          {/* Current location display */}
          {hasLocation && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <MapPinIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">
                    {locationAddress || 'Location set'}
                  </p>
                  <p className="text-xs text-blue-700 mt-0.5">
                    Lat: {parseFloat(latitude).toFixed(6)}, Lng: {parseFloat(longitude).toFixed(6)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Google Places Picker */}
          <div className="space-y-4">
            <GooglePlacesLocationPicker
              value={{ latitude, longitude }}
              onChange={(location) => {
                setLatitude(String(location.latitude))
                setLongitude(String(location.longitude))
                setLocationAddress(location.address || '')
              }}
              label={hasLocation ? 'Update Location' : 'Set School Location *'}
              placeholder="Search for your school..."
            />

            <div className="flex justify-end">
              <Button
                onClick={handleUpdateLocation}
                disabled={saving || !latitude || !longitude || (parseFloat(latitude) === 0 && parseFloat(longitude) === 0)}
                loading={saving}
              >
                {saving ? 'Saving...' : hasLocation ? 'Update Location' : 'Save Location'}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default SchoolProfilePage
